import { Checkbox } from '@timelyapp/react';
import Avatar from 'components/Avatar';
import type { DropdownOption } from 'components/DropdownListWithOptions';
import DurationFormat from 'components/DurationFormat';
import DiagnosticResourceId from 'components/dev-tools/DiagnosticResourceId';
import RichTooltip from 'components/RichTooltip';
import { TableWidgetProjectBlock } from 'components/superreports/widgets/table/blocks/TableWidgetProjectBlock';
import { Td, TdActions, Tr } from 'components/ui-blocks/table';
import type { SyntheticEvent } from 'react';
import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { forecastOptionsFactory } from 'routes/forecast/factories/forecast';
import {
  deleteForecastId,
  toggleCompleteOnForecastId,
} from 'routes/forecast/redux/modules/forecast.actions';
import { batchSelectionActions } from 'routes/forecast/redux/modules/forecast.types';
import type { EnrichedForecast } from 'routes/forecast/types/forecast';
import type { ThunkDispatch } from 'types/redux';
import { prefixHexColor } from 'utils/colors';
import { getProjectColor } from 'utils/project';
import { truncateText } from 'utils/text-helper';
import ForecastStateIndicator from '../ForecastStateIndcator';
import TableIdenticator from './TableIdenticator';

type ForecastTrContext =
  | { type: 'assignee'; assigneeId: number | null }
  | { type: 'project'; projectId: number };

interface ForecastTrProps {
  selected?: boolean;
  forecast: EnrichedForecast;
  showAssignee?: boolean;
  showProject?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
  /** How many px to pad the left side to create indentation? */
  indent?: number;
  onClickForecast: (id: number, mode?: 'edit' | 'view') => void;
  onOptionClick?: (option: DropdownOption) => void;
  context: ForecastTrContext;
}
export const ForecastTr = (props: ForecastTrProps) => {
  const { forecast, showAssignee, showProject, canUpdate, canDelete, context } = props;
  const [isShowingOptions, setShowingOptions] = useState(false);
  const dispatch = useDispatch<ThunkDispatch>();

  const relevantAllocations = useMemo(
    () =>
      Object.entries(forecast.allocationByUser)
        .filter(([userId]) => {
          if (context.type === 'assignee') {
            return +userId === context.assigneeId;
          }
          return true;
        })
        .map(([, allocation]) => allocation),
    [forecast.allocationByUser, context]
  );

  const divisor = (forecast?.users.length || 1) / (relevantAllocations.length || 1);
  const estimatedMinutes = forecast.estimated_minutes / divisor;

  if (!forecast || !forecast.meta) {
    return null;
  }

  /**
   * May want to re-consider the placement of these click handlers.
   * Perhaps move further up, and/or generalize a "onClickOption" thunk
   * as a more generalized action handler for re-use across forecasts
   * in other contexts (e.g, on calendar, on timelines, in lists, etc, etc)
   *
   */
  function handleOptionClick(option: DropdownOption) {
    if (option.action === 'delete') {
      dispatch(deleteForecastId(forecast.id));
    }
    if (option.action === 'toggleCompleted') {
      dispatch(
        toggleCompleteOnForecastId(forecast.id, {
          interaction: 'Mark Completed',
        })
      );
    }
    if (option.action === 'edit') {
      props.onClickForecast(props.forecast.id, 'edit');
    }
    if (option.action === 'visitExternal' && option.meta?.url) {
      window.open(option.meta.url, '_blank', 'noopener,noreferrer');
    }
  }

  function handleShowDropdown(event?: SyntheticEvent) {
    event?.preventDefault();
    setShowingOptions(true);
  }

  const colSpan = () => {
    const assigneeColspan = showAssignee ? 1 : 0;
    const projectColspan = showProject ? 1 : 0;
    return 4 - assigneeColspan - projectColspan;
  };

  const hasWarningSigns = forecast.meta.isOverdue;

  const stopPropagation = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const toggleSelection = () => {
    dispatch(
      batchSelectionActions.toggleSelection({
        selectionKey: 'forecastIds',
        value: props.forecast.id,
      })
    );
  };

  const options = forecastOptionsFactory(forecast, { canUpdate, canDelete, tic: forecast.tic });

  return (
    <Tr
      variant={hasWarningSigns ? 'danger' : forecast?.completed_at ? 'success' : undefined}
      interactive
      onClick={() => props.onClickForecast(props.forecast.id)}
      onContextMenu={handleShowDropdown}
    >
      <Td colSpan={colSpan()}>
        <TableIdenticator indent={props.indent} style={{ width: 50 }}>
          {canUpdate && (
            <Checkbox
              style={{ marginRight: 15 }}
              onClick={stopPropagation}
              onChange={toggleSelection}
              checked={props.selected}
            />
          )}
          <ForecastStateIndicator
            color={prefixHexColor(getProjectColor(forecast.project))}
            isComplete={!!forecast.completed_at}
            ticMeta={forecast.tic}
          />
        </TableIdenticator>
        {forecast.title.length >= 60 ? (
          <RichTooltip
            tooltip={forecast.title.length >= 60 ? <span>{forecast.title}</span> : undefined}
            position="right"
          >
            <span className="fs-mask">
              <DiagnosticResourceId resource="task" id={forecast?.id} />{' '}
              {truncateText(forecast.title, 60)} &nbsp;
            </span>
          </RichTooltip>
        ) : (
          <span className="fs-mask">
            <DiagnosticResourceId resource="task" id={forecast?.id} />{' '}
            {truncateText(forecast.title, 60)} &nbsp;
          </span>
        )}
      </Td>
      {showAssignee && (
        <Td>
          {forecast.users.length > 0
            ? forecast.users.map(user => (
                <>
                  <Avatar
                    shape="circle"
                    src={user.avatar.medium_retina}
                    name={user.name}
                    fallbackToInitials
                    className="fs-exclude"
                  />
                  <span style={{ marginLeft: 5 }} className="fs-mask">
                    <DiagnosticResourceId resource="user" id={user.id} />
                    {user.name}
                  </span>
                </>
              ))
            : 'Unassigned'}
        </Td>
      )}
      {showProject && (
        <Td>
          {!!forecast?.project && (
            <TableWidgetProjectBlock project={forecast.project} ticMeta={forecast.tic} />
          )}
        </Td>
      )}
      <Td colSpan={2}>
        {forecast.completed_at ? (
          <div>
            <strong>Done</strong>
          </div>
        ) : (
          <div>
            <strong>
              <DurationFormat
                value={estimatedMinutes ?? 0}
                format="h[h] m[m]"
                trim="both"
                zeroPlaceholder="0m"
              />
            </strong>{' '}
            est
            <small>{forecast?.meta.progressedPercent}% logged</small>
          </div>
        )}
      </Td>
      {/* <Td>{forecast?.meta.last_checkin_status}</Td> */}
      <TdActions<DropdownOption>
        isOpen={isShowingOptions}
        onClose={() => setShowingOptions(false)}
        onOpen={handleShowDropdown}
        options={options}
        onClick={handleOptionClick}
      />
    </Tr>
  );
};

export default React.memo(ForecastTr);
